import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject } from '@angular/core';
import { ChartData, ChartOptions, ChartType, TooltipItem } from 'chart.js';
import { ChartModule } from 'primeng/chart';
import { SimulationGraph, SimulationGraphPoint, SimulationGraphSeries } from '../../../interfaces/Simulation';
import { MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatButtonModule } from '@angular/material/button';

@Component({
  selector: 'simulation-graphs',
  standalone: true,
  imports: [
    CommonModule,
    ChartModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatDialogModule,
    MatButtonModule
  ],
  templateUrl: './simulation-graphs.component.html',
  styleUrl: './simulation-graphs.component.scss'
})
export class SimulationGraphsComponent implements AfterViewInit{

  data = inject<SimulationGraph[]>(MAT_DIALOG_DATA);

  currentChart: SimulationGraph | undefined;
  chartType: ChartType = 'bar';

  chartData: ChartData = {
    labels: [],
    datasets: []
  };

  chartOptions: ChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom'
      },
      tooltip: {
        enabled: true
      }
    },
    interaction: {
      mode: 'index',
      intersect: false
    },
    scales: {
      x: {
        title: { display: true, text: 'Day' }
      },
      y: {
        title: { display: true, text: 'Time' }
      }
    }
  };

  ngAfterViewInit(): void {
    if (this.data.length > 0) {
      setTimeout(()=>{
        this.changeChart(this.data[0]);
      });
    }
  }

  changeChart(graph: SimulationGraph){
    this.currentChart = graph;
    this.chartType = this.getChartType(graph);
    this.chartData = this.buildChartData(graph);
    this.configureGraph();
  }

  private getChartType(graph: SimulationGraph): ChartType {
    return graph.chart_type === 'scatter' ? 'scatter' : 'bar';
  }

  private buildChartData(graph: SimulationGraph): ChartData {
    switch (graph.chart_type) {
      case 'scatter':
        return this.buildScatterData(graph);
      case 'histogram':
        return this.buildHistogramData(graph);
      case 'bar':
      case 'grouped_bar':
      default:
        return this.buildBarData(graph);
    }
  }

  private buildBarData(graph: SimulationGraph): ChartData {
    const labels = graph.x_axis.values ?? [];
    const datasets = graph.series.map((series: SimulationGraphSeries) => ({
      label: series.name.toString(),
      data: series.values ?? [],
      fill: false,
      tension: 0.4
    }));

    return {
      labels,
      datasets
    };
  }

  private buildScatterData(graph: SimulationGraph): ChartData {
    const datasets = graph.series.map((series: SimulationGraphSeries) => ({
      label: series.name.toString(),
      data: (series.points ?? []).map((point: SimulationGraphPoint) => ({
        ...point,
        x: point.x,
        y: point.y
      })),
      showLine: false,
      pointRadius: 5,
      pointHoverRadius: 7
    }));

    return { datasets };
  }

  private buildHistogramData(graph: SimulationGraph): ChartData {
    const values = graph.series.flatMap((series: SimulationGraphSeries) => series.values ?? []);
    const bins = this.createHistogramBins(values);

    return {
      labels: bins.map((bin) => bin.label),
      datasets: [
        {
          label: graph.series[0]?.name ?? graph.title,
          data: bins.map((bin) => bin.count)
        }
      ]
    };
  }

  private createHistogramBins(values: number[]): { label: string; count: number }[] {
    if (values.length === 0) {
      return [];
    }

    const min = Math.min(...values);
    const max = Math.max(...values);

    if (min === max) {
      return [{ label: this.formatBinLabel(min, max), count: values.length }];
    }

    const binCount = Math.min(20, Math.max(5, Math.ceil(Math.sqrt(values.length))));
    const binSize = (max - min) / binCount;
    const bins = Array.from({ length: binCount }, (_, index) => {
      const start = min + (index * binSize);
      const end = index === binCount - 1 ? max : start + binSize;
      return {
        start,
        end,
        label: this.formatBinLabel(start, end),
        count: 0
      };
    });

    values.forEach((value) => {
      const binIndex = value === max
        ? binCount - 1
        : Math.floor((value - min) / binSize);
      bins[binIndex].count += 1;
    });

    return bins.map(({ label, count }) => ({ label, count }));
  }

  private formatBinLabel(start: number, end: number): string {
    return `${this.formatBinValue(start)} - ${this.formatBinValue(end)}`;
  }

  private formatBinValue(value: number): string {
    return Number.isInteger(value) ? value.toString() : value.toFixed(2);
  }

  configureGraph() {
    const rs = getComputedStyle(document.documentElement);
    const chartText = rs.getPropertyValue('--chart-text').trim();
    const chartGrid = rs.getPropertyValue('--chart-grid').trim();
    this.chartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'bottom'
        },
        tooltip: {
          enabled: true
        }
      },
      interaction: {
        mode: this.chartType === 'scatter' ? 'nearest' : 'index',
        intersect: this.chartType === 'scatter'
      },
      scales: {
        x: {
          type: this.chartType === 'scatter' ? 'linear' : 'category',
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: this.currentChart!.x_axis.label, color: chartText }
        },
        y: {
          ticks: { color: chartText },
          grid: { color: chartGrid },
          title: { display: true, text: this.currentChart!.y_axis.label, color: chartText }
        }
      }
    };

    if (this.chartType === 'scatter') {
      this.chartOptions.plugins!.tooltip = {
        enabled: true,
        callbacks: {
          label: (context: TooltipItem<ChartType>) => {
            const point = context.raw as SimulationGraphPoint;
            const label = point.label ?? context.dataset.label ?? '';
            return `${label}: (${point.x}, ${point.y})`;
          }
        }
      };
    }
  }
}
